/**
 * Format a raw bank/account label into a clean, single-font display name.
 *
 * Statement labels often carry masking noise before the last four digits,
 * e.g. "Chase xx1633", "Chase ••••1633", "Chase ****1633" or "Chase ...1633".
 * This normalizes all of those to a tidy "Chase 1633".
 *
 * - Strips trailing masking tokens (bullet/star/dot runs, ellipsis, or x-runs)
 *   that sit between the bank name and the trailing digits.
 * - Keeps only the last four digits of any trailing number.
 * - Leaves labels without trailing digits (e.g. "Chase Checking") essentially
 *   untouched, beyond removing stray masking glyphs.
 */
export function formatAccountName(raw: string | null | undefined): string {
  if (!raw) return '';
  const label = String(raw).trim();

  const match = label.match(/(\d{4,})\s*$/);
  if (!match) {
    return label.replace(/[•*]/g, '').replace(/\s{2,}/g, ' ').trim();
  }

  const last4 = match[1].slice(-4);
  let name = label.slice(0, match.index).trim();
  // Strip trailing noise REPEATEDLY: masking glyph runs, x-runs, separators,
  // AND stray digit groups — the remains of a full card number ("USAA 4270
  // XXXX XXXX 1961 xx1961") belong to the number, not the name. One pass per
  // layer until the tail is clean.
  let prev = '';
  while (name !== prev) {
    prev = name;
    name = name
      .replace(/[•*·.…\u2026\-\s]+$/g, '')
      .replace(/\s*x{2,}\s*$/gi, '')
      .replace(/\s\d{2,4}$/g, '')
      .trim();
  }
  name = name.replace(/\s{2,}/g, ' ').trim();

  return name ? `${name} ${last4}` : last4;
}
